import { del, get, post, put } from '../../shared/api/apiService';

export const DIAM_API_PATH = '/diam/api/v1';

export const DIAM_TEAM_ID_HEADER = 'Team-Id';

export const DIAM_TEAM_ID = '01a16976-fef2-7894-9fc6-0cb3fb6dc9bc';

export const DIAM_PAGE_LIMIT = 1000;

export type DiamIdentityType = 'HUMAN' | 'M2M';

export type DiamRoleType = 'FUNCTIONAL' | 'TEAM';

export type DiamRoleAccessDuration = 'PT1H' | 'P1D' | 'P7D';

export type DiamRoleStatus = 'ACTIVE' | 'DEPRECATED';

export type DiamRoleInclusionType = 'ROLE' | 'ATTRIBUTES';

export interface DiamPageSortField {
  field: string;
  direction: 'ASC' | 'DESC';
}

export interface DiamPageRequest<TFilter> {
  page?: number;
  limit?: number;
  sort?: DiamPageSortField[];
  filter?: TFilter;
}

export interface DiamPageResponse<T> {
  elements?: T[];
  total?: number;
  totalPages?: number;
  page?: number;
  isFirst?: boolean;
  isLast?: boolean;
}

export interface DiamRoleFilter {
  type?: DiamRoleType;
  status?: DiamRoleStatus;
  teamId?: string;
  search?: string;
}

export interface DiamTeamRoleFilter {
  search?: string;
  type?: DiamRoleType;
  status?: DiamRoleStatus;
}

export type DiamIdentityTeamScope = 'ALL' | 'CHILD' | 'SINGLE';

export interface DiamIdentityFilter {
  search?: string;
  type?: DiamIdentityType;
  isInternal?: boolean;
  teamId?: string;
  teamScope?: DiamIdentityTeamScope;
}

export interface DiamRoleAttribute {
  id: string;
  name: string;
  requiredModifiers?: string[];
  optionalModifiers?: string[];
}

export interface DiamRoleInclusion {
  type: DiamRoleInclusionType;
  roleName?: string;
  roleCode?: string | null;
  roleId?: string;
  attributes?: DiamRoleAttribute[];
}

export interface DiamRoleInclusionRequest {
  type: DiamRoleInclusionType;
  roleId: string;
  attributeIds?: string[];
}

export interface DiamTeamRoleCreateRequest {
  name: string;
  description?: string;
  inclusions: DiamRoleInclusionRequest[];
  modifiers?: DiamModifierValues;
}

export type DiamTeamRoleUpdateRequest = DiamTeamRoleCreateRequest;

export interface DiamRoleDetails {
  roleId: string;
  type: DiamRoleType;
  status?: DiamRoleStatus;
  teamId?: string | null;
  team?: DiamTeamReference | null;
  name: string;
  code?: string | null;
  description?: string;
  directModifiers?: DiamModifierValues;
  requiredModifiers?: string[];
  optionalModifiers?: string[];
  attributes?: DiamRoleAttribute[];
  grantAuthority?: string;
  inclusions?: DiamRoleInclusion[];
  createdDate?: string;
  lastModifiedDate?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface DiamTeamReference {
  id: string;
  name: string;
}

export interface DiamIdentity {
  id: string;
  type: DiamIdentityType;
  isInternal?: boolean;
  isActive?: boolean;
  externalId?: string;
  email?: string;
  phone?: string;
  name?: string;
  teams?: DiamTeamReference[];
  createdDate?: string;
  lastModifiedDate?: string;
}

export type DiamModifierValues = Record<string, string>;

export interface DiamRoleAccess {
  id: string;
  name: string;
  description?: string;
  type: DiamRoleType;
  applicationContextId?: string | null;
  applicationContextName?: string | null;
  permissions?: string[];
  modifiers?: DiamModifierValues;
  requestPending: boolean;
}

export interface DiamMyTeamRoles {
  teamId: string;
  teamName: string;
  grantedRoles?: DiamRoleAccess[];
  availableRoles?: DiamRoleAccess[];
}

export interface DiamRoleAccessRequestCreate {
  teamId: string;
  roleId: string;
  justification: string;
  duration: DiamRoleAccessDuration | null;
}

export interface DiamTeamMembershipSuspension {
  isSuspended: boolean;
  reason?: string;
}

export interface DiamTeamMembership {
  teamId: string;
  identity?: Pick<DiamIdentity, 'id' | 'type' | 'name' | 'email'>;
  suspension?: DiamTeamMembershipSuspension;
  roles?: DiamRoleDetails[];
  createdDate?: string;
  lastModifiedDate?: string;
}

export interface DiamEffectiveAttribute {
  id: string;
  name: string;
}

export interface DiamEffectiveGrant {
  assignmentId?: string;
  attribute?: DiamEffectiveAttribute;
  rolePath?: Array<{ id: string; name: string; type?: DiamRoleType }>;
}

export interface DiamEffectiveAccess {
  teamId: string;
  identityId: string;
  grants?: DiamEffectiveGrant[];
}

export interface DiamTeamFilter {
  name?: string;
  parentId?: string;
}

export interface DiamResolvedModifierValue {
  value?: string;
  source?: string;
}

export interface DiamTeam {
  id: string;
  name: string;
  identitiesCount?: number;
  modifiers?: Record<string, DiamResolvedModifierValue>;
  parent?: DiamTeamReference | null;
  createdDate?: string;
  lastModifiedDate?: string;
}

export interface DiamApplicationContext {
  id: string;
  key?: string;
  name: string;
  description?: string;
}

export interface DiamTeamTree {
  id: string;
  name: string;
  identitiesCount?: number;
  children?: DiamTeamTree[];
}

export interface DiamTeamTreeResponse {
  teams?: DiamTeamTree[];
}

export type DiamModifierType = 'account' | 'location' | 'expires';

export interface DiamLocationModifier {
  name: string;
  code: string;
  children?: DiamLocationModifier[];
}

export interface DiamModifierGroup {
  type: DiamModifierType;
  items?: Array<string | DiamLocationModifier>;
}

export interface DiamModifiersResponse {
  modifiers?: DiamModifierGroup[];
}

export type DiamInvitationStatus =
  | 'PENDING'
  | 'ACCEPTED'
  | 'REVOKED'
  | 'EXPIRED';

export interface DiamInvitationFilter {
  status?: DiamInvitationStatus;
  email?: string;
  teamId?: string;
}

export interface DiamInvitationRole {
  roleId: string;
  name: string;
}

export interface DiamInvitation {
  id: string;
  email: string;
  teamId?: string;
  team?: DiamTeamReference;
  identityId?: string | null;
  status: DiamInvitationStatus;
  roles?: DiamInvitationRole[];
  expiresAt?: string;
}

export interface DiamInvitationLink {
  link: string;
}

export interface DiamInvitationCreateRequest {
  email: string;
  teamId: string;
  roleIds: string[];
}

export interface DiamInvitationCreateResponse {
  invitations?: DiamInvitation[];
}

export type DiamAuditAction = 'CREATE' | 'UPDATE' | 'DELETE';

export type DiamAuditResourceType =
  | 'TEAM'
  | 'TEAM_MEMBERSHIP'
  | 'IDENTITY'
  | 'IDENTITY_DETAILS'
  | 'INVITATION'
  | 'REGISTRATION_REQUEST'
  | 'ROLE_ACCESS_REQUEST'
  | 'WHITE_LIST_DOMAIN'
  | 'ROLE'
  | 'APPLICATION_CONTEXT'
  | 'TEAM_APPLICATION_CONTEXT'
  | 'MEMBERSHIP_ROLE_ASSIGNMENT';

export interface DiamAuditLogFilter {
  dateFrom?: string;
  dateTo?: string;
  actions?: DiamAuditAction[];
  resourceTypes?: DiamAuditResourceType[];
  createdByName?: string;
  teamId?: string;
  teamScope?: DiamIdentityTeamScope;
}

export interface DiamAuditLogTeam {
  id?: string;
  name?: string;
  applicationNames?: string[];
}

export interface DiamAuditLog {
  id: string;
  createdByName: string;
  createdDate: string;
  teamId: string;
  team?: DiamAuditLogTeam | null;
  action: DiamAuditAction;
  resourceType: DiamAuditResourceType;
}

export const diamQueryKeys = {
  myTeamRoles: (teamId: string) => ['diam', 'me', 'roles', teamId] as const,
  roles: (filter: DiamRoleFilter) => ['diam', 'roles', filter] as const,
  role: (roleId: string) => ['diam', 'roles', roleId] as const,
  teams: (filter: DiamTeamFilter) => ['diam', 'teams', 'page', filter] as const,
  team: (teamId: string) => ['diam', 'teams', teamId] as const,
  teamTree: (parentId: string) => ['diam', 'teams', 'tree', parentId] as const,
  modifiers: (types: readonly DiamModifierType[]) =>
    ['diam', 'modifiers', types] as const,
  teamApplicationContexts: (teamId: string) =>
    ['diam', 'teams', teamId, 'application-contexts'] as const,
  teamRoles: (teamId: string, filter: DiamTeamRoleFilter) =>
    ['diam', 'teams', teamId, 'roles', filter] as const,
  identities: (filter: DiamIdentityFilter) =>
    ['diam', 'identities', filter] as const,
  identity: (identityId: string) => ['diam', 'identities', identityId] as const,
  teamMembership: (teamId: string, identityId: string) =>
    ['diam', 'teams', teamId, 'members', identityId] as const,
  invitations: (filter: DiamInvitationFilter) =>
    ['diam', 'invitations', filter] as const,
  auditLogs: (filter: DiamAuditLogFilter) =>
    ['diam', 'audit-logs', filter] as const,
};

export function diamTeamHeaders(): Record<string, string> {
  return { [DIAM_TEAM_ID_HEADER]: DIAM_TEAM_ID };
}

export async function getMyTeamRoles(
  apiUrl: string,
  authToken: string,
): Promise<DiamMyTeamRoles> {
  return get<DiamMyTeamRoles>('/identities/me/roles', apiUrl, authToken, {
    params: { teamId: DIAM_TEAM_ID },
    headers: diamTeamHeaders(),
  });
}

export async function createMyRoleAccessRequest(
  apiUrl: string,
  authToken: string,
  payload: DiamRoleAccessRequestCreate,
): Promise<void> {
  await post<void>(
    '/identities/me/role-access-requests',
    { ...payload, teamId: DIAM_TEAM_ID },
    apiUrl,
    authToken,
    {
      headers: diamTeamHeaders(),
    },
  );
}

export async function getRolesPage(
  apiUrl: string,
  authToken: string,
  request: DiamPageRequest<DiamRoleFilter>,
): Promise<DiamPageResponse<DiamRoleDetails>> {
  return post<DiamPageResponse<DiamRoleDetails>>(
    `teams/${DIAM_TEAM_ID}/roles/page`,
    request,
    apiUrl,
    authToken,
    { headers: diamTeamHeaders() },
  );
}

export async function getRoleById(
  apiUrl: string,
  authToken: string,
  roleId: string,
): Promise<DiamRoleDetails> {
  return get<DiamRoleDetails>(`/roles/${roleId}`, apiUrl, authToken, {
    headers: diamTeamHeaders(),
  });
}

export async function getTeamRolesPage(
  apiUrl: string,
  authToken: string,
  teamId: string,
  request: DiamPageRequest<DiamTeamRoleFilter>,
): Promise<DiamPageResponse<DiamRoleDetails>> {
  return post<DiamPageResponse<DiamRoleDetails>>(
    `/teams/${teamId}/roles/page`,
    request,
    apiUrl,
    authToken,
    { headers: diamTeamHeaders() },
  );
}

export async function createTeamRole(
  apiUrl: string,
  authToken: string,
  teamId: string,
  request: DiamTeamRoleCreateRequest,
): Promise<DiamRoleDetails> {
  return post<DiamRoleDetails>(
    `/teams/${teamId}/roles`,
    request,
    apiUrl,
    authToken,
    { headers: diamTeamHeaders() },
  );
}

export async function updateTeamRole(
  apiUrl: string,
  authToken: string,
  teamId: string,
  roleId: string,
  request: DiamTeamRoleUpdateRequest,
): Promise<DiamRoleDetails> {
  return put<DiamRoleDetails>(
    `/teams/${teamId}/roles/${roleId}`,
    request,
    apiUrl,
    authToken,
    { headers: diamTeamHeaders() },
  );
}

export async function deleteTeamRole(
  apiUrl: string,
  authToken: string,
  roleId: string,
): Promise<void> {
  await del<void>(`/teams/${DIAM_TEAM_ID}/roles/${roleId}`, apiUrl, authToken, {
    headers: diamTeamHeaders(),
  });
}

export async function getTeamsPage(
  apiUrl: string,
  authToken: string,
  request: DiamPageRequest<DiamTeamFilter>,
): Promise<DiamPageResponse<DiamTeam>> {
  return post<DiamPageResponse<DiamTeam>>(
    '/teams/page',
    request,
    apiUrl,
    authToken,
    { headers: diamTeamHeaders() },
  );
}

export async function getTeamById(
  apiUrl: string,
  authToken: string,
  teamId: string,
): Promise<DiamTeam> {
  return get<DiamTeam>(`/teams/${teamId}`, apiUrl, authToken, {
    headers: diamTeamHeaders(),
  });
}

export async function getTeamTree(
  apiUrl: string,
  authToken: string,
  parentId: string,
): Promise<DiamTeamTreeResponse> {
  return post<DiamTeamTreeResponse>(
    '/teams/tree',
    { parentId },
    apiUrl,
    authToken,
    { headers: diamTeamHeaders() },
  );
}

export async function getModifiers(
  apiUrl: string,
  authToken: string,
  types: DiamModifierType[],
): Promise<DiamModifiersResponse> {
  return post<DiamModifiersResponse>(
    '/modifiers/page',
    { filter: { types } },
    apiUrl,
    authToken,
    { headers: diamTeamHeaders() },
  );
}

export async function createTeam(
  apiUrl: string,
  authToken: string,
  request: {
    parentId: string;
    name: string;
    applicationContextIds: string[];
    modifiers?: DiamModifierValues;
  },
): Promise<DiamTeam> {
  return post<DiamTeam>('/teams', request, apiUrl, authToken, {
    headers: diamTeamHeaders(),
  });
}

export async function updateTeam(
  apiUrl: string,
  authToken: string,
  teamId: string,
  request: { name: string; modifiers?: DiamModifierValues },
): Promise<DiamTeam> {
  return put<DiamTeam>(`/teams/${teamId}`, request, apiUrl, authToken, {
    headers: diamTeamHeaders(),
  });
}

export async function replaceTeamApplicationContexts(
  apiUrl: string,
  authToken: string,
  teamId: string,
  applicationContextIds: string[],
): Promise<void> {
  await put<void>(
    `/teams/${teamId}/application-contexts`,
    { applicationContextIds },
    apiUrl,
    authToken,
    { headers: diamTeamHeaders() },
  );
}

export async function deleteTeam(
  apiUrl: string,
  authToken: string,
  teamId: string,
): Promise<void> {
  await del<void>(`/teams/${teamId}`, apiUrl, authToken, {
    headers: diamTeamHeaders(),
  });
}

export async function getTeamApplicationContextsPage(
  apiUrl: string,
  authToken: string,
  teamId: string,
  request: DiamPageRequest<{ key?: string; name?: string }>,
): Promise<DiamPageResponse<DiamApplicationContext>> {
  return post<DiamPageResponse<DiamApplicationContext>>(
    `/teams/${teamId}/application-contexts/page`,
    request,
    apiUrl,
    authToken,
    { headers: diamTeamHeaders() },
  );
}

export async function getInvitationsPage(
  apiUrl: string,
  authToken: string,
  request: DiamPageRequest<DiamInvitationFilter>,
): Promise<DiamPageResponse<DiamInvitation>> {
  return post<DiamPageResponse<DiamInvitation>>(
    '/invitations/page',
    request,
    apiUrl,
    authToken,
    { headers: diamTeamHeaders() },
  );
}

export async function createInvitation(
  apiUrl: string,
  authToken: string,
  request: DiamInvitationCreateRequest,
): Promise<DiamInvitationCreateResponse> {
  return post<DiamInvitationCreateResponse>(
    '/invitations',
    request,
    apiUrl,
    authToken,
    { headers: diamTeamHeaders() },
  );
}

export async function getAuditLogsPage(
  apiUrl: string,
  authToken: string,
  request: DiamPageRequest<DiamAuditLogFilter>,
): Promise<DiamPageResponse<DiamAuditLog>> {
  return post<DiamPageResponse<DiamAuditLog>>(
    '/audit-logs/page',
    request,
    apiUrl,
    authToken,
    { headers: diamTeamHeaders() },
  );
}

export async function getInvitationLink(
  apiUrl: string,
  authToken: string,
  invitationId: string,
): Promise<DiamInvitationLink> {
  return get<DiamInvitationLink>(
    `/invitations/${invitationId}/link`,
    apiUrl,
    authToken,
    { headers: diamTeamHeaders() },
  );
}

export async function resendInvitation(
  apiUrl: string,
  authToken: string,
  invitationId: string,
): Promise<DiamInvitation> {
  return post<DiamInvitation>(
    `/invitations/${invitationId}/resend`,
    {},
    apiUrl,
    authToken,
    { headers: diamTeamHeaders() },
  );
}

export async function revokeInvitation(
  apiUrl: string,
  authToken: string,
  invitationId: string,
): Promise<void> {
  await del<void>(`/invitations/${invitationId}`, apiUrl, authToken, {
    headers: diamTeamHeaders(),
  });
}

export async function getIdentitiesPage(
  apiUrl: string,
  authToken: string,
  request: DiamPageRequest<DiamIdentityFilter>,
): Promise<DiamPageResponse<DiamIdentity>> {
  return post<DiamPageResponse<DiamIdentity>>(
    '/identities/page',
    request,
    apiUrl,
    authToken,
    { headers: diamTeamHeaders() },
  );
}

export async function getIdentityById(
  apiUrl: string,
  authToken: string,
  identityId: string,
): Promise<DiamIdentity> {
  return get<DiamIdentity>(`/identities/${identityId}`, apiUrl, authToken, {
    headers: diamTeamHeaders(),
  });
}

export async function updateIdentityStatus(
  apiUrl: string,
  authToken: string,
  identityId: string,
  isActive: boolean,
): Promise<void> {
  await put<void>(
    `/identities/${identityId}/status`,
    { isActive },
    apiUrl,
    authToken,
    { headers: diamTeamHeaders() },
  );
}

export async function getTeamMembership(
  apiUrl: string,
  authToken: string,
  identityId: string,
): Promise<DiamTeamMembership> {
  return get<DiamTeamMembership>(
    `/teams/${DIAM_TEAM_ID}/members/${identityId}`,
    apiUrl,
    authToken,
    { headers: diamTeamHeaders() },
  );
}

export async function updateTeamMembershipSuspension(
  apiUrl: string,
  authToken: string,
  identityId: string,
  isSuspended: boolean,
  reason: string,
): Promise<void> {
  await put<void>(
    `/teams/${DIAM_TEAM_ID}/members/${identityId}/suspension`,
    { isSuspended, reason },
    apiUrl,
    authToken,
    { headers: diamTeamHeaders() },
  );
}

export async function removeMembershipRole(
  apiUrl: string,
  authToken: string,
  identityId: string,
  roleId: string,
): Promise<void> {
  await del<void>(
    `/teams/${DIAM_TEAM_ID}/members/${identityId}/roles/${roleId}`,
    apiUrl,
    authToken,
    { headers: diamTeamHeaders() },
  );
}

export interface DiamMembershipRoleAssignRequest {
  id: string;
  modifiers?: DiamModifierValues;
}

export async function assignMembershipRoles(
  apiUrl: string,
  authToken: string,
  identityId: string,
  roles: DiamMembershipRoleAssignRequest[],
): Promise<void> {
  await post<void>(
    `/teams/${DIAM_TEAM_ID}/members/${identityId}/roles`,
    { roles },
    apiUrl,
    authToken,
    { headers: diamTeamHeaders() },
  );
}
